import React, { useState, useEffect } from 'react';
import {
  Image as ImageIcon, Package, Plus,
  Loader2, Upload, CheckCircle2, Trash2, Pencil,
  ShieldCheck, ChevronRight, AlertCircle, FileText, LayoutList,
  MessageSquare, Send, RotateCcw, Search, X, FileDown, BarChart3
} from 'lucide-react';

// --- FIREBASE SETUP ---
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, onSnapshot, addDoc, deleteDoc, updateDoc, doc, query, orderBy, limit } from 'firebase/firestore';
import Papa from 'papaparse';

// Reads from CRA env vars (REACT_APP_* — must be set at build time and
// prefixed exactly that way for CRA to expose them to the browser bundle).
// Falls back to the hardcoded project values below if no .env is present,
// so this still works out of the box for local dev.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyBqJl6aAGyv30kyylkeeY-sGiZPPhvin1Y",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "indusfootwear-chatbot.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "indusfootwear-chatbot",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "indusfootwear-chatbot.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "381921009814",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:381921009814:web:f6045a8640af9e7ffca2b7"
};

const APP_ID = 'indus-portal';
const UPLOAD_ENDPOINT = 'https://indus-chatbot.indusfootwear.com/upload.php';

let app, auth, db;
try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
} catch (e) {
  console.error("Firebase init error:", e);
}

// --- BRAND COLORS & MASTERS ---
const COLORS = { primary: '#1d3c45', secondary: '#2db2a5' };

const AUDIENCE_MASTERS = ['Men', 'Women', 'Kids Boy', 'Kids Girl', 'Unisex'];

// ─────────────────────────────────────────────────────────────────────────────
// compressImageToBlob
// Converts ANY image format (WebP, PNG, GIF, JPEG) → compressed JPEG Blob.
// Returns a raw binary Blob — NOT base64 — so we can send via FormData
// with zero encode/decode overhead. ~33% smaller payload than base64.
//
//  maxWidth : 800px  — good for WhatsApp catalogue cards
//  quality  : 0.82   — visually lossless for footwear photos at this size
//  White bg fill     — required because JPEG has no alpha channel
// ─────────────────────────────────────────────────────────────────────────────
const compressImageToBlob = (file, maxWidth = 800, quality = 0.82) => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      let w = img.width;
      let h = img.height;
      if (w > maxWidth) {
        h = Math.round((h * maxWidth) / w);
        w = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width  = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');

      // White background — flattens WebP/PNG transparency before JPEG conversion
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(objectUrl);

      // toBlob = async, raw binary, no base64 step at all
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('canvas.toBlob failed')),
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Image failed to load for compression'));
    };

    img.src = objectUrl;
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// App — root component
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [products, setProducts] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);

  useEffect(() => {
    if (!auth) { setAuthChecked(true); return; }
    // No auto sign-in of any kind. The account must already exist
    // (created manually in the Firebase console) and be logged in
    // explicitly via the form below.
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      setCurrentUser(user);
      setAuthChecked(true);
      if (user) setIsLoading(false);
    });
    return () => unsub();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginBusy(true);
    try {
      await signInWithEmailAndPassword(auth, loginEmail.trim(), loginPassword);
      setLoginPassword('');
    } catch (err) {
      setLoginError('Incorrect email or password.');
    } finally {
      setLoginBusy(false);
    }
  };

  const handleLogout = () => {
    signOut(auth);
    setProducts([]);
    setCategoriesData([]);
  };

  useEffect(() => {
    if (!db || !isAuthenticated) return;
    try {
      const productsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'products');
      const unsubProducts = onSnapshot(productsRef, (snap) => {
        setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setIsLoading(false);
      }, (err) => { console.error("Firestore error:", err); setIsLoading(false); });

      const catsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'categories');
      const unsubCats = onSnapshot(catsRef, (snap) => {
        setCategoriesData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });

      return () => { unsubProducts(); unsubCats(); };
    } catch (err) {
      console.error("Firestore Init Error:", err);
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: COLORS.secondary }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <form onSubmit={handleLogin} className="bg-white rounded-xl shadow-md p-8 w-full max-w-sm border-t-4" style={{ borderColor: COLORS.secondary }}>
          <div className="flex flex-col items-center mb-6">
            <img src="/indus-logo.png" alt="Indus Footwear" className="h-12 object-contain mb-2"
              onError={e => { e.target.style.display = 'none'; }} />
            <h1 className="font-extrabold text-2xl tracking-tighter" style={{ color: COLORS.primary }}>
              indus<span style={{ color: COLORS.secondary }}>.</span>
            </h1>
            <p className="text-[10px] font-bold text-gray-500 tracking-widest uppercase mt-1">Admin Portal</p>
          </div>
          <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Email</label>
          <input
            type="email"
            required
            autoFocus
            value={loginEmail}
            onChange={e => setLoginEmail(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 text-sm focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': COLORS.secondary }}
          />
          <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Password</label>
          <input
            type="password"
            required
            value={loginPassword}
            onChange={e => setLoginPassword(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 text-sm focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': COLORS.secondary }}
          />
          {loginError && <p className="text-red-600 text-xs font-semibold mb-4">{loginError}</p>}
          <button
            type="submit"
            disabled={loginBusy}
            className="w-full py-2.5 rounded-lg text-sm font-bold text-white shadow-sm disabled:opacity-60"
            style={{ backgroundColor: COLORS.primary }}
          >
            {loginBusy ? 'Signing in...' : 'Secure Login'}
          </button>
        </form>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: COLORS.secondary }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col font-sans">
      <header className="bg-white shadow-sm px-4 md:px-8 py-3 flex justify-between items-center sticky top-0 z-10 border-b-4" style={{ borderColor: COLORS.secondary }}>
        <div className="flex items-center space-x-3">
          <img src="/indus-logo.png" alt="Indus Footwear" className="h-10 object-contain"
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
          <div style={{ display: 'none', backgroundColor: COLORS.primary }} className="p-2 rounded-lg items-center justify-center">
            <ShieldCheck className="text-white w-5 h-5" />
          </div>
          <div>
            <h1 className="font-extrabold text-2xl leading-tight tracking-tighter" style={{ color: COLORS.primary }}>
              indus<span style={{ color: COLORS.secondary }}>.</span>
            </h1>
            <p className="text-[10px] font-bold text-gray-500 tracking-widest uppercase">Admin Portal</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center px-4 py-2 rounded-lg text-sm font-bold text-white shadow-sm" style={{ backgroundColor: COLORS.primary }}>
            <ShieldCheck className="w-4 h-4 mr-2" /> Admin Dashboard
          </span>
          <button
            onClick={handleLogout}
            className="px-3 py-2 rounded-lg text-xs font-bold text-gray-600 border border-gray-300 hover:bg-gray-50"
          >
            Log out
          </button>
        </div>
      </header>

      <main className="flex-1 w-full mx-auto flex flex-col overflow-hidden">
        <AdminView products={products} categoriesData={categoriesData} db={db} />
      </main>

      <footer className="py-3 text-center border-t border-gray-200 bg-white">
        <p className="text-[11px] font-bold tracking-widest uppercase" style={{ color: COLORS.primary }}>
          © {new Date().getFullYear()} INDUS FOOTWEAR. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AdminView — tabs, forms, upload logic
// ─────────────────────────────────────────────────────────────────────────────
function AdminView({ products, categoriesData, db }) {
  const [activeTab, setActiveTab] = useState('add_product');
  const [editingProductId, setEditingProductId] = useState(null);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [dynamicColors, setDynamicColors] = useState([]);

  // Activity log — lazy-loaded only when the tab is actually opened
  // (not on every admin session) to avoid unnecessary Firestore reads.
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityResultFilter, setActivityResultFilter] = useState('all');
  const [activityViewMode, setActivityViewMode] = useState('customers'); // 'customers' | 'log'

  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  // Starts the live listener the first time the Activity tab is opened,
  // then keeps it subscribed permanently (survives switching to other
  // tabs and back) — previously it unsubscribed on every tab-away and
  // never resubscribed, which is why the data looked frozen/stale until
  // a full page reload. activityRefreshKey lets the manual Refresh
  // button force a clean re-subscribe on demand too.
  useEffect(() => {
    if (activeTab === 'activity' && !activityLoaded) {
      setActivityLoaded(true);
    }
  }, [activeTab, activityLoaded]);

  useEffect(() => {
    if (!activityLoaded || !db) return;
    setActivityLoading(true);
    const q = query(
      collection(db, 'artifacts', APP_ID, 'public', 'data', 'activity_log'),
      orderBy('timestamp', 'desc'),
      limit(500)
    );
    const unsub = onSnapshot(q, (snap) => {
      setActivityLog(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setActivityLoading(false);
    }, (err) => {
      console.error('Activity log fetch failed:', err);
      setActivityLoading(false);
    });
    return () => unsub();
  }, [activityLoaded, db, activityRefreshKey]);

  // Product form
  const [productData, setProductData] = useState({ name: '', category: '', audience: '' });
  const [colorsList, setColorsList] = useState([{ color: '', file: null, previewUrl: '', existingUrl: '' }]);

  // Inventory tab — search + multi-category filter + sort
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryCategoryFilters, setInventoryCategoryFilters] = useState([]); // multi-select
  const [inventorySort, setInventorySort] = useState('newest'); // 'newest' | 'oldest' | 'name'
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [bulkCategoryValue, setBulkCategoryValue] = useState('');
  const [bulkCategoryUpdating, setBulkCategoryUpdating] = useState(false);
  const [bulkCategoryProgress, setBulkCategoryProgress] = useState(null); // {done, total}

  const toggleProductSelection = (id) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkCategoryUpdate = async () => {
    if (!db || selectedProductIds.size === 0) return;
    if (!bulkCategoryValue) { alert('Choose a category to apply first.'); return; }
    if (!window.confirm(`Move ${selectedProductIds.size} selected product(s) to "${bulkCategoryValue}"?`)) return;

    setBulkCategoryUpdating(true);
    const ids = Array.from(selectedProductIds);
    setBulkCategoryProgress({ done: 0, total: ids.length });

    let done = 0;
    for (const id of ids) {
      try {
        await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'products', id), {
          category: bulkCategoryValue,
          updatedAt: new Date().toISOString(),
        });
      } catch (err) {
        console.error(`Failed to update category for product ${id}:`, err);
      }
      done++;
      setBulkCategoryProgress({ done, total: ids.length });
    }

    setBulkCategoryUpdating(false);
    setBulkCategoryProgress(null);
    setSelectedProductIds(new Set());
    setBulkCategoryValue('');
  };

  // Bulk upload (CSV) state
  const [bulkParsedProducts, setBulkParsedProducts] = useState([]); // grouped {name, category, audience, variations:[{color,image}]}[]
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkParseErrors, setBulkParseErrors] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // {done, total}
  const [bulkResults, setBulkResults] = useState(null); // {added, skipped, failed}

  // Category form
  const [catForm, setCatForm] = useState({ id: null, name: '', pdfLink: '' });

  // Bot Tester state
  const [botEndpoint, setBotEndpoint] = useState('http://localhost:3000');
  const [botAdminKey, setBotAdminKey] = useState('');
  const [testFromNumber, setTestFromNumber] = useState('919999999999'); // Cloud API uses bare E.164, no "whatsapp:" prefix
  const [chatLog, setChatLog] = useState([]); // { sender: 'user'|'bot', text, mediaUrl }[]
  const [testInput, setTestInput] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { index, total, pct } | null
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // Category Master (the "Category Master" tab) is now the single source
    // of truth for which categories can be assigned to a product. Old
    // hardcoded defaults and one-off categories left over on individual
    // products no longer clutter the dropdown — only categories explicitly
    // added in Category Master appear. (If a product's current category
    // was since removed from Category Master, it's still preserved on that
    // product's own record and shown as an option only while editing it —
    // see the category <select> in the product form.)
    const cats = new Set(categoriesData.map(c => c.name));
    setDynamicCategories([...cats].sort());
  }, [categoriesData]);

  useEffect(() => {
    const cols = new Set();
    products.forEach(p => (p.variations || []).forEach(v => { if (v.color) cols.add(v.color); }));
    setDynamicColors([...cols].sort());
  }, [products]);

  // ── Color row handlers ────────────────────────────────────────────────────
  const handleColorImage = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const newColors = [...colorsList];
    newColors[index] = { ...newColors[index], file, previewUrl: URL.createObjectURL(file) };
    setColorsList(newColors);
  };
  const addColor    = () => setColorsList([...colorsList, { color: '', file: null, previewUrl: '', existingUrl: '' }]);
  const removeColor = (idx) => setColorsList(colorsList.filter((_, i) => i !== idx));
  const updateColorName = (idx, val) => {
    const n = [...colorsList]; n[idx].color = val; setColorsList(n);
  };

  // ── UPLOAD — binary FormData (fastest path) ───────────────────────────────
  // Flow:
  //   1. compressImageToBlob  → JPEG Blob (client-side, no base64)
  //   2. FormData.append      → raw binary multipart POST
  //   3. XHR with progress    → live % bar
  //   4. PHP move_uploaded_file → atomic OS rename, no decode work
  //   5. Returns https:// URL → stored in Firestore → Twilio fetches directly
  // ─────────────────────────────────────────────────────────────────────────
  const uploadFile = (file, colorIndex, totalNewFiles) => {
    return new Promise(async (resolve) => {
      try {
        // Step 1 — Compress & convert to JPEG Blob (handles WebP/PNG/GIF/JPEG)
        const jpegBlob = await compressImageToBlob(file);

        // Step 2 — Build multipart FormData with raw binary blob
        const formData = new FormData();
        const safeFilename = file.name.replace(/\.[^.]+$/, '.jpg');
        formData.append('image', jpegBlob, safeFilename);

        // Step 3 — XHR for upload progress tracking
        const xhr = new XMLHttpRequest();
        xhr.open('POST', UPLOAD_ENDPOINT);
        // ⚠ Do NOT set Content-Type — browser sets it automatically with multipart boundary

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress({
              index: colorIndex + 1,
              total: totalNewFiles,
              pct: Math.round((e.loaded / e.total) * 100)
            });
          }
        };

        xhr.onload = () => {
          try {
            const result = JSON.parse(xhr.responseText);
            if (result.status === 'success' && result.url) {
              console.log(`✅ Color ${colorIndex + 1} uploaded: ${result.url}`);
              resolve(result.url);
            } else {
              console.error(`❌ upload.php error:`, result.message);
              resolve(null); // null = skip variation, don't store broken base64
            }
          } catch (parseErr) {
            console.error('Response parse error:', parseErr, xhr.responseText);
            resolve(null);
          }
        };

        xhr.onerror = () => {
          console.error(`❌ Network error on color ${colorIndex + 1}`);
          resolve(null);
        };

        xhr.send(formData);

      } catch (compressionErr) {
        console.error('Compression error:', compressionErr);
        resolve(null);
      }
    });
  };

  // ── Product submit ────────────────────────────────────────────────────────
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!db) return;

    const trimmedName = productData.name.trim();
    if (products.some(p => p.id !== editingProductId && String(p.name || '').trim().toLowerCase() === trimmedName.toLowerCase())) {
      alert(`A product named "${trimmedName}" already exists.`);
      return;
    }

    const safeColors = colorsList.filter(c => c.color.trim() !== '');
    if (safeColors.length === 0) { alert("Add at least one color with an image."); return; }
    const missingImg = safeColors.find(c => !c.file && !c.existingUrl);
    if (missingImg) { alert(`Upload an image for color "${missingImg.color}".`); return; }

    setIsSubmitting(true);
    setUploadProgress(null);

    try {
      const finalVariations = [];
      const newFiles = safeColors.filter(c => c.file);
      let uploadCount = 0;

      for (const c of safeColors) {
        let imageUrl = c.existingUrl || null;

        if (c.file) {
          imageUrl = await uploadFile(c.file, uploadCount, newFiles.length);
          uploadCount++;
          if (!imageUrl) {
            alert(`Upload failed for color "${c.color}". Check your server and try again.`);
            setIsSubmitting(false);
            setUploadProgress(null);
            return;
          }
        }

        finalVariations.push({ color: c.color, image: imageUrl });
      }

      setUploadProgress(null);

      const payload = {
        name: trimmedName,
        category: productData.category.trim(),
        audience: productData.audience.trim(),
        articleId: trimmedName,
        variations: finalVariations,
        updatedAt: new Date().toISOString()
      };

      if (editingProductId) {
        await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'products', editingProductId), payload);
        setSuccessMsg('Product updated successfully!');
      } else {
        payload.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'artifacts', APP_ID, 'public', 'data', 'products'), payload);
        setSuccessMsg('Product added successfully!');
      }

      resetForm();
      setTimeout(() => setSuccessMsg(''), 3000);

    } catch (err) {
      console.error("Submit error:", err);
      alert(`Failed to save product: ${err.message}`);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  // Common shorthand used in photo filenames — expanded automatically so
  // the stored color name is always the full word customers/staff expect,
  // regardless of how the file was named. Add more abbreviations here as
  // you come across them; matching is whole-word and case-insensitive.
  const COLOR_ABBREVIATIONS = {
    'BLK': 'Black', 'BRN': 'Brown', 'WHT': 'White', 'MHD': 'Mehandi',
    'GRY': 'Grey', 'GRN': 'Green', 'BLU': 'Blue', 'B.GRN': 'B.Green',
    'CHRY': 'Cherry', 'TN': 'Tan', 'MRN': 'Maroon', 'ONG': 'Orange',
    'PNK': 'Pink', 'PRPL': 'Purple', 'YLW': 'Yellow', 'GLD': 'Gold',
    'SLV': 'Silver', 'BG': 'Beige', 'NVY': 'Navy',
  };

  const expandColorAbbreviations = (colorStr) => {
    const words = colorStr
      .split(/\s+/)
      .map(word => {
        const plainKey = word.toUpperCase().replace(/[.,]$/, '');
        return COLOR_ABBREVIATIONS[word.toUpperCase()] || COLOR_ABBREVIATIONS[plainKey] || word;
      });
    // Dual/multi-tone colors (e.g. filename "BLACK WHITE" or "BLK WHT")
    // are joined with "/" rather than a plain space, so they read as one
    // combined color name — "Black/White" — matching how dual-tone shades
    // are conventionally written, instead of looking like two separate words.
    return words.join('/');
  };

  const resetForm = () => {
    setProductData({ name: '', category: '', audience: '' });
    setColorsList([{ color: '', file: null, previewUrl: '', existingUrl: '' }]);
    setEditingProductId(null);
    setUploadProgress(null);
  };

  const handleBulkPhotoFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setBulkPhotoResults(null);
    const parsed = files.map(file => {
      const base = file.name.replace(/\.[^.]+$/, '').trim();
      const spaceIdx = base.indexOf(' ');
      if (spaceIdx === -1) {
        return { file, article: null, color: null, error: `"${file.name}" — expected "ARTICLE COLOR.jpg", no space found` };
      }
      const article = base.slice(0, spaceIdx).trim();
      const rawColor = base.slice(spaceIdx + 1).trim();
      if (!article || !rawColor) {
        return { file, article: null, color: null, error: `"${file.name}" — couldn't read article/color` };
      }
      const color = expandColorAbbreviations(rawColor);
      return { file, article, color, error: null };
    });
    setBulkPhotoFiles(parsed);
  };

  const handleBulkPhotoUpload = async () => {
    if (!db || bulkPhotoFiles.length === 0) return;
    if (!bulkPhotoCategory) { alert('Please choose a category for this batch before uploading.'); return; }

    const validFiles = bulkPhotoFiles.filter(f => !f.error);
    if (validFiles.length === 0) { alert('No valid files to upload — check filenames match "ARTICLE COLOR.jpg".'); return; }

    setBulkPhotoProcessing(true);
    setBulkPhotoProgress({ done: 0, total: validFiles.length });

    // Group files by article number (case-insensitive) so multiple colors
    // for the same article land as variations on one product.
    const groups = {};
    validFiles.forEach(f => {
      const key = f.article.toLowerCase();
      if (!groups[key]) groups[key] = { article: f.article, items: [] };
      groups[key].items.push(f);
    });

    let created = 0, updated = 0, failed = 0;
    let doneCount = 0;
    const productsByName = new Map(products.map(p => [String(p.name || '').trim().toLowerCase(), p]));

    for (const key of Object.keys(groups)) {
      const { article, items } = groups[key];
      const existing = productsByName.get(key);

      try {
        // Upload every image in this group first
        const uploadedVariations = [];
        for (const item of items) {
          const url = await uploadFile(item.file, doneCount, validFiles.length);
          doneCount++;
          setBulkPhotoProgress({ done: doneCount, total: validFiles.length });
          if (!url) {
            failed++;
            continue;
          }
          uploadedVariations.push({ color: item.color, image: url });
        }

        if (uploadedVariations.length === 0) continue;

        if (existing) {
          // Merge: replace image for matching colors, append new ones
          const mergedVariations = [...(existing.variations || [])];
          uploadedVariations.forEach(nv => {
            const idx = mergedVariations.findIndex(v => String(v.color).trim().toLowerCase() === nv.color.toLowerCase());
            if (idx >= 0) mergedVariations[idx] = { ...mergedVariations[idx], image: nv.image };
            else mergedVariations.push(nv);
          });
          await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'products', existing.id), {
            variations: mergedVariations,
            updatedAt: new Date().toISOString(),
          });
          updated++;
        } else {
          await addDoc(collection(db, 'artifacts', APP_ID, 'public', 'data', 'products'), {
            name: article,
            articleId: article,
            category: bulkPhotoCategory,
            audience: bulkPhotoAudience,
            variations: uploadedVariations,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
          created++;
        }
      } catch (err) {
        console.error(`Bulk photo upload failed for article "${article}":`, err);
        failed++;
      }
    }

    setBulkPhotoResults({ created, updated, failed });
    setBulkPhotoProcessing(false);
    setBulkPhotoFiles([]);
  };

  // ── Bulk upload (CSV) ─────────────────────────────────────────────────────
  // Simplified format for fast large-batch entry (built for 1000s of products
  // where photos aren't ready yet): CSV only needs `name` and `colors`
  // (colors separated by comma or semicolon, e.g. "Black; Brown; Grey").
  // Category and audience are picked ONCE above the upload widget and applied
  // to every row in the file — no need to repeat them per row, and no image
  // URLs are required at all. Products land in Firestore with each color
  // variation present but its image left blank; go to Single Upload
  // (or Edit on the Inventory card) afterwards to attach each color's photo
  // whenever it's ready — nothing blocks the bulk step on images being done.
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkAudience, setBulkAudience] = useState('');

  // Bulk Photos (filename-driven) state — files named "<article> <color>.jpg"
  // e.g. "16501 BLACK.jpg", "44107 BLACK RED.jpg" get parsed automatically:
  // article number becomes the product name/articleId, everything after the
  // first space becomes the color. Matches an existing product by name if
  // one exists (adding/updating that color), otherwise creates a new product.
  const [bulkPhotoCategory, setBulkPhotoCategory] = useState('');
  const [bulkPhotoAudience, setBulkPhotoAudience] = useState('');
  const [bulkPhotoFiles, setBulkPhotoFiles] = useState([]); // [{file, article, color, error}]
  const [bulkPhotoProcessing, setBulkPhotoProcessing] = useState(false);
  const [bulkPhotoProgress, setBulkPhotoProgress] = useState(null); // {done, total}
  const [bulkPhotoResults, setBulkPhotoResults] = useState(null); // {created, updated, failed}

  // Export the current inventory (respecting active search/category filters)
  // as an Excel-compatible spreadsheet, for offline two-step verification —
  // cross-checking what's actually in the database against physical stock
  // or a printed checklist. One row per product; colors and their image
  // status are listed in a single column so it stays readable in Excel
  // without needing a messy one-row-per-color layout.
  const exportInventoryToExcel = (productsToExport) => {
    if (!productsToExport || productsToExport.length === 0) {
      alert('Nothing to export — the current filtered list is empty.');
      return;
    }

    const rows = productsToExport.map(p => {
      const variations = p.variations || [];
      const colorsWithStatus = variations
        .map(v => {
          const hasImage = v.image && v.image.startsWith('http');
          return `${v.color}${hasImage ? '' : ' (NO IMAGE)'}`;
        })
        .join('; ');
      const missingCount = variations.filter(v => !(v.image && v.image.startsWith('http'))).length;

      return {
        'Article / Name': p.name || '',
        'Category': p.category || '',
        'Audience': p.audience || '',
        'Total Colors': variations.length,
        'Colors (image status)': colorsWithStatus,
        'Missing Images': missingCount,
        'Uploaded On': p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN') : '',
      };
    });

    const csv = Papa.unparse(rows);
    // Prepend UTF-8 BOM so Excel (especially on Windows) opens it correctly
    // instead of mangling special characters like ✓/₹ if they appear.
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `indus_inventory_export_${dateStamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSampleCsv = () => {
    const sample = 'name,colors\n2225,Black; Brown\n4259,Black; Tan; Grey\n';
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'indus_bulk_upload_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkFileName(file.name);
    setBulkResults(null);
    setBulkParseErrors([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const errors = [];
        const grouped = {}; // name -> product

        results.data.forEach((row, idx) => {
          const rowNum = idx + 2; // account for header row
          const name = (row.name || row.articleId || '').trim();
          const colorsRaw = (row.colors || row.color || '').trim();

          if (!name) { errors.push(`Row ${rowNum}: missing "name"`); return; }
          if (!colorsRaw) { errors.push(`Row ${rowNum} ("${name}"): missing "colors"`); return; }

          const colorNames = colorsRaw.split(/[;,]/).map(c => expandColorAbbreviations(c.trim())).filter(Boolean);
          if (colorNames.length === 0) { errors.push(`Row ${rowNum} ("${name}"): no valid colors found`); return; }

          const key = name.toLowerCase();
          if (!grouped[key]) {
            grouped[key] = {
              name,
              variations: colorNames.map(color => ({ color, image: '' })),
            };
          } else {
            // Same name appearing on multiple rows — merge additional colors in.
            colorNames.forEach(color => {
              if (!grouped[key].variations.some(v => v.color.toLowerCase() === color.toLowerCase())) {
                grouped[key].variations.push({ color, image: '' });
              }
            });
          }
        });

        setBulkParsedProducts(Object.values(grouped));
        setBulkParseErrors(errors);
      },
      error: (err) => {
        setBulkParseErrors([`Failed to parse CSV: ${err.message}`]);
        setBulkParsedProducts([]);
      }
    });
  };

  const handleBulkUpload = async () => {
    if (!db || bulkParsedProducts.length === 0) return;
    if (!bulkCategory) { alert('Please choose a category for this batch before uploading.'); return; }
    setBulkUploading(true);
    setBulkProgress({ done: 0, total: bulkParsedProducts.length });

    let added = 0, skipped = 0, failed = 0;
    const existingNames = new Set(products.map(p => String(p.name || '').trim().toLowerCase()));

    for (let i = 0; i < bulkParsedProducts.length; i++) {
      const prod = bulkParsedProducts[i];
      const key = prod.name.trim().toLowerCase();

      if (existingNames.has(key)) {
        skipped++;
      } else {
        try {
          await addDoc(collection(db, 'artifacts', APP_ID, 'public', 'data', 'products'), {
            name: prod.name,
            category: bulkCategory,
            audience: bulkAudience,
            articleId: prod.name,
            variations: prod.variations,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
          existingNames.add(key); // guard against dupes within the same CSV
          added++;
        } catch (err) {
          console.error(`Bulk upload failed for "${prod.name}":`, err);
          failed++;
        }
      }
      setBulkProgress({ done: i + 1, total: bulkParsedProducts.length });
    }

    setBulkResults({ added, skipped, failed });
    setBulkUploading(false);
    setBulkParsedProducts([]);
    setBulkFileName('');
  };

  const handleEditProduct = (product) => {
    setEditingProductId(product.id);
    setProductData({ name: product.name || '', category: product.category || '', audience: product.audience || '' });
    const editColors = (product.variations || []).map(v => ({
      color: v.color || '', file: null, previewUrl: '', existingUrl: v.image || ''
    }));
    setColorsList(editColors.length ? editColors : [{ color: '', file: null, previewUrl: '', existingUrl: '' }]);
    setActiveTab('add_product');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteProduct = async (id) => {
    if (!db || !window.confirm('Delete this product?')) return;
    try { await deleteDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'products', id)); }
    catch (err) { console.error(err); }
  };

  // ── Category handlers ─────────────────────────────────────────────────────
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!db) return;
    setIsSubmitting(true);
    try {
      const payload = { name: catForm.name.trim(), pdfLink: catForm.pdfLink.trim() };
      if (catForm.id) {
        await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'categories', catForm.id), payload);
        setSuccessMsg('Category updated!');
      } else {
        await addDoc(collection(db, 'artifacts', APP_ID, 'public', 'data', 'categories'), payload);
        setSuccessMsg('Category added!');
      }
      setCatForm({ id: null, name: '', pdfLink: '' });
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error(err); alert('Failed to save category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCategory   = (cat) => { setCatForm({ id: cat.id, name: cat.name, pdfLink: cat.pdfLink || '' }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const handleDeleteCategory = async (id) => {
    if (!db) return;
    try { await deleteDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'categories', id)); }
    catch (err) { console.error(err); }
  };

  // ── Bot Tester — posts JSON {Body, From} to the Flask server's dedicated
  //    /whatsapp-test route. That route runs the message through the exact
  //    same handler the real Meta webhook uses, but intercepts every
  //    outbound send_* call instead of hitting graph.facebook.com, and
  //    returns them as a JSON "outbox" array. No Meta credentials or
  //    public URL needed — fully local. See server.py's whatsapp_test().
  const sendTestMessage = async (overrideBody) => {
    const body = overrideBody !== undefined ? overrideBody : testInput;
    if (!body.trim() || isSendingTest) return;

    setChatLog(prev => [...prev, { sender: 'user', text: body }]);
    setTestInput('');
    setIsSendingTest(true);

    try {
      const res = await fetch(`${botEndpoint.replace(/\/$/, '')}/whatsapp-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(botAdminKey ? { 'X-Admin-Key': botAdminKey } : {}),
        },
        body: JSON.stringify({ Body: body, From: testFromNumber }),
      });
      if (res.status === 401) {
        setChatLog(prev => [...prev, { sender: 'bot', text: '⚠️ Unauthorized (401) — check the Admin Key field below matches ADMIN_API_KEY on the server.' }]);
        setIsSendingTest(false);
        return;
      }
      const data = await res.json();
      const outbox = data.outbox || [];

      if (outbox.length === 0) {
        setChatLog(prev => [...prev, { sender: 'bot', text: '(no reply sent — check your Flask server logs)' }]);
      } else {
        for (const item of outbox) {
          if (item.type === 'text') {
            setChatLog(prev => [...prev, { sender: 'bot', text: item.body }]);
          } else if (item.type === 'media' || item.type === 'document') {
            setChatLog(prev => [...prev, { sender: 'bot', text: item.caption || '', mediaUrl: item.media_url }]);
          } else if (item.type === 'buttons') {
            const labels = (item.buttons || []).map(b => b.title).join('  |  ');
            setChatLog(prev => [...prev, { sender: 'bot', text: `${item.body}\n\n[buttons: ${labels}]` }]);
          } else if (item.type === 'list') {
            const labels = (item.rows || []).map(r => r.title).join(', ');
            setChatLog(prev => [...prev, { sender: 'bot', text: `${item.body}\n\n[list "${item.button_label}": ${labels}]` }]);
          }
        }
      }
    } catch (err) {
      setChatLog(prev => [...prev, { sender: 'bot', text: `⚠️ Request failed: ${err.message}. Check botEndpoint URL and that the server is running/CORS-enabled.` }]);
    } finally {
      setIsSendingTest(false);
    }
  };

  const resetTestSession = () => {
    setChatLog([]);
    sendTestMessage('menu');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden">

      {/* ── Sidebar (desktop) ── */}
      <div className="w-64 bg-white border-r border-gray-200 hidden md:flex flex-col h-full shadow-sm z-10">
        <div className="p-4 border-b border-gray-100">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Inventory Management</p>
          <div className="space-y-1">
            {[
              { key: 'add_product', icon: <Upload className="w-4 h-4 mr-3" />, label: 'Single Upload' },
              { key: 'bulk_upload', icon: <FileText className="w-4 h-4 mr-3" />, label: 'Bulk Upload' },
              { key: 'bulk_photos', icon: <ImageIcon className="w-4 h-4 mr-3" />, label: 'Bulk Photos' },
              { key: 'inventory',   icon: <Package  className="w-4 h-4 mr-3" />, label: 'View Inventory' },
              { key: 'categories',  icon: <LayoutList className="w-4 h-4 mr-3" />, label: 'Category Master' },
              { key: 'bot_tester',  icon: <MessageSquare className="w-4 h-4 mr-3" />, label: 'Bot Tester' },
              { key: 'activity',    icon: <BarChart3 className="w-4 h-4 mr-3" />, label: 'Activity' },
            ].map(({ key, icon, label }) => (
              <button key={key} onClick={() => { setActiveTab(key); setSuccessMsg(''); setErrorMsg(''); }}
                className={`w-full flex items-center px-3 py-2.5 text-sm font-bold rounded-lg transition-colors ${activeTab === key ? 'bg-teal-50 text-teal-700' : 'text-gray-600 hover:bg-gray-50'}`}
                style={{ color: activeTab === key ? COLORS.secondary : undefined }}>
                {React.cloneElement(icon, { style: { color: activeTab === key ? COLORS.secondary : undefined } })}
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">

        {/* Mobile tabs */}
        <div className="flex bg-white rounded-xl shadow-sm border border-gray-200 p-1 mb-6 md:hidden">
          {[['add_product','Upload'],['bulk_upload','Bulk'],['bulk_photos','Photos'],['inventory','Inventory'],['categories','Categories'],['bot_tester','Bot Test'],['activity','Activity']].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`flex-1 py-2 text-xs font-bold rounded-lg ${activeTab === key ? 'bg-gray-800 text-white' : 'text-gray-500'}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Alerts */}
        {successMsg && (
          <div className="mb-6 p-4 bg-teal-50 text-teal-800 border-l-4 rounded-r-lg shadow-sm flex items-center font-bold" style={{ borderColor: COLORS.secondary }}>
            <CheckCircle2 className="w-5 h-5 mr-3" style={{ color: COLORS.secondary }} />{successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="mb-6 p-4 bg-red-50 text-red-800 border-l-4 border-red-500 rounded-r-lg shadow-sm flex items-center font-bold">
            <AlertCircle className="w-5 h-5 mr-3 text-red-500" />{errorMsg}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            UPLOAD TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'add_product' && (
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="flex items-center text-sm text-gray-500 mb-2 font-bold uppercase tracking-wider">
              <span>Home</span><ChevronRight className="w-4 h-4 mx-1" /><span>Products</span><ChevronRight className="w-4 h-4 mx-1" />
              <span style={{ color: COLORS.primary }}>{editingProductId ? 'Edit Product' : 'Create'}</span>
            </div>

            <form onSubmit={handleProductSubmit} className="space-y-6">

              {/* Basic details card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="font-bold text-gray-800">Basic Details</h3>
                  {!editingProductId && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded font-bold">Article ID = Name</span>}
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Product Name / Art No. <span className="text-red-500">*</span></label>
                    <input required type="text" value={productData.name}
                      onChange={e => setProductData({ ...productData, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:outline-none bg-white"
                      placeholder="e.g. 2225" />
                    {productData.name.trim() && products.some(p =>
                      p.id !== editingProductId && String(p.name || '').trim().toLowerCase() === productData.name.trim().toLowerCase()
                    ) && <p className="text-[10px] text-red-500 mt-1 font-semibold">Name already exists.</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Category <span className="text-red-500">*</span></label>
                    <input required list="cat-list" value={productData.category}
                      onChange={e => setProductData({ ...productData, category: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:outline-none bg-white"
                      placeholder="Select or type..." />
                    <datalist id="cat-list">{dynamicCategories.map(c => <option key={c} value={c} />)}</datalist>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">Audience <span className="text-red-500">*</span></label>
                    <select required value={productData.audience}
                      onChange={e => setProductData({ ...productData, audience: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:outline-none bg-white">
                      <option value="">Select Audience</option>
                      {AUDIENCE_MASTERS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Color variations card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="font-bold text-gray-800">Color / Image Variations</h3>
                  <button type="button" onClick={addColor}
                    className="text-xs font-bold flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100">
                    <Plus className="w-3 h-3 mr-1" /> Add Color
                  </button>
                </div>

                <p className="px-6 pt-4 text-xs text-gray-500">
                  WebP, PNG, JPG all accepted —
                  <span className="font-semibold text-teal-700 ml-1">auto-compressed + converted to JPEG for fastest WhatsApp delivery.</span>
                </p>

                {/* Upload progress bar */}
                {uploadProgress && (
                  <div className="mx-6 mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <div className="flex justify-between mb-1">
                      <span className="text-xs font-bold text-teal-700">
                        Uploading image {uploadProgress.index} of {uploadProgress.total}…
                      </span>
                      <span className="text-xs font-bold text-teal-700">{uploadProgress.pct}%</span>
                    </div>
                    <div className="w-full bg-teal-100 rounded-full h-2">
                      <div className="h-2 rounded-full transition-all duration-150"
                        style={{ width: `${uploadProgress.pct}%`, backgroundColor: COLORS.secondary }} />
                    </div>
                  </div>
                )}

                <div className="p-0 overflow-x-auto">
                  <table className="w-full text-sm text-left border-collapse min-w-[640px]">
                    <thead className="bg-white border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-3 font-bold w-1/3">Color <span className="text-red-500">*</span></th>
                        <th className="px-6 py-3 font-bold w-1/2">Image <span className="text-red-500">*</span></th>
                        <th className="px-6 py-3 font-bold text-center">Del</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {colorsList.map((c, idx) => (
                        <tr key={idx} className="bg-white hover:bg-gray-50/50">
                          <td className="px-6 py-4 align-top">
                            <input required list="col-list" type="text" value={c.color}
                              onChange={e => updateColorName(idx, e.target.value)}
                              className="w-full border border-gray-300 rounded bg-white p-2.5 text-sm focus:ring-2 focus:outline-none"
                              placeholder="e.g. BLACK" />
                            <datalist id="col-list">{dynamicColors.map(col => <option key={col} value={col} />)}</datalist>
                          </td>
                          <td className="px-6 py-4 align-top">
                            <div className="flex flex-col sm:flex-row gap-3 items-start">
                              <label className="cursor-pointer bg-white border border-gray-300 text-gray-700 font-medium text-xs px-4 py-2.5 rounded shadow-sm hover:bg-gray-50 whitespace-nowrap">
                                {c.file ? `✓ ${c.file.name.slice(0, 20)}` : 'Choose File'}
                                <input type="file" className="sr-only" accept="image/*"
                                  onChange={e => handleColorImage(e, idx)}
                                  required={!c.previewUrl && !c.existingUrl} />
                              </label>
                              {(c.previewUrl || c.existingUrl) && (
                                <img src={c.previewUrl || c.existingUrl} alt="preview"
                                  className="h-12 w-24 object-cover rounded border border-gray-200 bg-gray-100 shadow-sm" />
                              )}
                            </div>
                            {c.file && (
                              <p className="text-[10px] text-teal-600 mt-1 font-semibold">
                                Binary JPEG upload → fastest transfer
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4 align-top text-center">
                            {idx > 0 && (
                              <button type="button" onClick={() => removeColor(idx)}
                                className="p-2 bg-red-100 text-red-600 rounded hover:bg-red-200">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Submit row */}
              <div className="flex justify-end pt-2 gap-4">
                {editingProductId && (
                  <button type="button" onClick={resetForm} disabled={isSubmitting}
                    className="px-8 py-3 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300">
                    Cancel Edit
                  </button>
                )}
                <button type="submit" disabled={isSubmitting}
                  className="px-8 py-3 text-white font-bold rounded-lg disabled:opacity-70 flex items-center shadow-md"
                  style={{ backgroundColor: COLORS.secondary }}>
                  {isSubmitting
                    ? <><Loader2 className="w-5 h-5 animate-spin mr-2" />
                        {uploadProgress
                          ? `Uploading ${uploadProgress.index}/${uploadProgress.total} (${uploadProgress.pct}%)`
                          : 'Saving to database…'}
                      </>
                    : <><CheckCircle2 className="w-5 h-5 mr-2" />{editingProductId ? 'Update Product' : 'Save Product'}</>
                  }
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            INVENTORY TAB
        ════════════════════════════════════════════════════════════════════ */}
        {/* ════════════════════════════════════════════════════════════════════
            BULK UPLOAD TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'bulk_upload' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold mb-2" style={{ color: COLORS.primary }}>Bulk Upload (CSV)</h2>
              <p className="text-sm text-gray-500 mb-6">
                Just <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">name</code> and{' '}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">colors</code> (semicolon or comma-separated, e.g. "Black; Brown; Grey") —
                no image URLs needed. Category and audience below apply to every product in this file. Add color photos afterwards from
                Inventory → Edit, whenever they're ready — nothing here waits on images.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Category for this batch *</label>
                  <input required list="bulk-cat-list" value={bulkCategory} onChange={e => setBulkCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': COLORS.secondary }} placeholder="e.g. Gents PU" />
                  <datalist id="bulk-cat-list">{dynamicCategories.map(c => <option key={c} value={c} />)}</datalist>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Audience (optional)</label>
                  <input value={bulkAudience} onChange={e => setBulkAudience(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': COLORS.secondary }} placeholder="e.g. Men" />
                </div>
              </div>

              <button onClick={downloadSampleCsv} type="button"
                className="text-xs font-bold flex items-center gap-1.5 mb-4 hover:underline" style={{ color: COLORS.secondary }}>
                <FileText className="w-3.5 h-3.5" /> Download sample CSV template
              </button>

              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl py-10 cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors">
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm font-bold text-gray-600">{bulkFileName || 'Click to select a CSV file'}</span>
                <input type="file" accept=".csv" onChange={handleBulkFileSelect} className="hidden" disabled={bulkUploading} />
              </label>

              {bulkParseErrors.length > 0 && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 font-bold text-sm mb-2">
                    <AlertCircle className="w-4 h-4" /> {bulkParseErrors.length} row(s) skipped
                  </div>
                  <ul className="text-xs text-red-600 space-y-1 max-h-40 overflow-y-auto">
                    {bulkParseErrors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                </div>
              )}

              {bulkParsedProducts.length > 0 && !bulkUploading && (
                <div className="mt-6">
                  <h3 className="font-bold text-sm mb-3" style={{ color: COLORS.primary }}>
                    Preview — {bulkParsedProducts.length} product(s) ready · category: <span style={{ color: COLORS.secondary }}>{bulkCategory || '(not set)'}</span>
                  </h3>
                  <div className="border border-gray-200 rounded-lg divide-y max-h-72 overflow-y-auto mb-4">
                    {bulkParsedProducts.map((p, i) => {
                      const isDup = products.some(existing => String(existing.name || '').trim().toLowerCase() === p.name.trim().toLowerCase());
                      return (
                        <div key={i} className="p-3 flex items-center justify-between text-sm">
                          <div>
                            <span className="font-bold">{p.name}</span>
                            <span className="text-gray-400 ml-2">{p.variations.map(v => v.color).join(', ')}</span>
                          </div>
                          {isDup && <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded flex-shrink-0 ml-2">Will skip (duplicate name)</span>}
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={handleBulkUpload}
                    disabled={!bulkCategory}
                    className="w-full py-3 rounded-lg text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: COLORS.secondary }}
                  >
                    <CheckCircle2 className="w-4 h-4" /> Upload {bulkParsedProducts.length} Product(s)
                  </button>
                  {!bulkCategory && <p className="text-xs text-red-600 font-semibold mt-2 text-center">Choose a category above first.</p>}
                </div>
              )}

              {bulkUploading && bulkProgress && (
                <div className="mt-6 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" style={{ color: COLORS.secondary }} />
                  <p className="text-sm font-bold text-gray-600">Uploading {bulkProgress.done} / {bulkProgress.total}...</p>
                </div>
              )}

              {bulkResults && (
                <div className="mt-6 bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm">
                  <p className="font-bold" style={{ color: COLORS.primary }}>Bulk upload complete</p>
                  <p className="text-gray-600 mt-1">
                    ✅ {bulkResults.added} added · ⏭ {bulkResults.skipped} skipped (duplicates) · {bulkResults.failed > 0 && `❌ ${bulkResults.failed} failed`}
                  </p>
                  {bulkResults.added > 0 && (
                    <p className="text-gray-500 mt-2 text-xs">
                      Products were added without images. Go to <b>View Inventory</b> — cards show which colors still need photos (orange ⚠ chips) — click Edit to add them.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            BULK PHOTOS TAB — filename-driven, fastest path (name+color+image
            in one shot, no CSV, no manual typing)
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'bulk_photos' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold mb-2" style={{ color: COLORS.primary }}>Bulk Photos — Fastest Way In</h2>
              <p className="text-sm text-gray-500 mb-6">
                Select a whole folder of photos at once. Name each file <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">ARTICLE COLOR.jpg</code> —
                e.g. <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">16501 BLACK.jpg</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">44107 BLACK RED.jpg</code>.
                Everything before the first space is the article/product name; everything after is the color (can have spaces, e.g. "BLACK RED").
                Same article across multiple files becomes one product with multiple color variations — all uploaded and saved in a single pass, no CSV needed at all.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Category for this batch *</label>
                  <input required list="bulk-photo-cat-list" value={bulkPhotoCategory} onChange={e => setBulkPhotoCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': COLORS.secondary }} placeholder="e.g. Gents PU" />
                  <datalist id="bulk-photo-cat-list">{dynamicCategories.map(c => <option key={c} value={c} />)}</datalist>
                  <p className="text-[11px] text-gray-400 mt-1">Only applied to newly created products — existing products keep their current category.</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Audience (optional)</label>
                  <input value={bulkPhotoAudience} onChange={e => setBulkPhotoAudience(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': COLORS.secondary }} placeholder="e.g. Men" />
                </div>
              </div>

              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl py-10 cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors">
                <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm font-bold text-gray-600">
                  {bulkPhotoFiles.length > 0 ? `${bulkPhotoFiles.length} file(s) selected` : 'Click to select all photos at once (multi-select supported)'}
                </span>
                <input type="file" accept="image/*" multiple onChange={handleBulkPhotoFileSelect} className="hidden" disabled={bulkPhotoProcessing} />
              </label>

              {bulkPhotoFiles.some(f => f.error) && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 font-bold text-sm mb-2">
                    <AlertCircle className="w-4 h-4" /> {bulkPhotoFiles.filter(f => f.error).length} file(s) couldn't be parsed
                  </div>
                  <ul className="text-xs text-red-600 space-y-1 max-h-32 overflow-y-auto">
                    {bulkPhotoFiles.filter(f => f.error).map((f, i) => <li key={i}>• {f.error}</li>)}
                  </ul>
                </div>
              )}

              {bulkPhotoFiles.length > 0 && !bulkPhotoProcessing && (
                <div className="mt-6">
                  <h3 className="font-bold text-sm mb-3" style={{ color: COLORS.primary }}>
                    Preview — {bulkPhotoFiles.filter(f => !f.error).length} photo(s) ready · category: <span style={{ color: COLORS.secondary }}>{bulkPhotoCategory || '(not set)'}</span>
                  </h3>
                  <div className="border border-gray-200 rounded-lg divide-y max-h-72 overflow-y-auto mb-4">
                    {Object.entries(
                      bulkPhotoFiles.filter(f => !f.error).reduce((acc, f) => {
                        (acc[f.article] = acc[f.article] || []).push(f.color);
                        return acc;
                      }, {})
                    ).map(([article, colors]) => {
                      const isExisting = products.some(p => String(p.name || '').trim().toLowerCase() === article.toLowerCase());
                      return (
                        <div key={article} className="p-3 flex items-center justify-between text-sm group">
                          <div>
                            <span className="font-bold">{article}</span>
                            <span className="text-gray-400 ml-2">{colors.join(', ')}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <span className={`text-[10px] font-bold px-2 py-1 rounded ${isExisting ? 'text-blue-600 bg-blue-50' : 'text-teal-600 bg-teal-50'}`}>
                              {isExisting ? 'Update existing' : 'New product'}
                            </span>
                            <button
                              onClick={() => setBulkPhotoFiles(prev => prev.filter(f => f.error || f.article !== article))}
                              className="text-gray-300 hover:text-red-500 transition-colors"
                              title={`Remove ${article} from this batch`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={handleBulkPhotoUpload}
                    disabled={!bulkPhotoCategory}
                    className="w-full py-3 rounded-lg text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: COLORS.secondary }}
                  >
                    <CheckCircle2 className="w-4 h-4" /> Upload {bulkPhotoFiles.filter(f => !f.error).length} Photo(s)
                  </button>
                  {!bulkPhotoCategory && <p className="text-xs text-red-600 font-semibold mt-2 text-center">Choose a category above first.</p>}
                </div>
              )}

              {bulkPhotoProcessing && bulkPhotoProgress && (
                <div className="mt-6 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" style={{ color: COLORS.secondary }} />
                  <p className="text-sm font-bold text-gray-600">Uploading {bulkPhotoProgress.done} / {bulkPhotoProgress.total}...</p>
                </div>
              )}

              {bulkPhotoResults && (
                <div className="mt-6 bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm">
                  <p className="font-bold" style={{ color: COLORS.primary }}>Bulk photo upload complete</p>
                  <p className="text-gray-600 mt-1">
                    ✅ {bulkPhotoResults.created} new product(s) created · 🔄 {bulkPhotoResults.updated} existing product(s) updated
                    {bulkPhotoResults.failed > 0 && ` · ❌ ${bulkPhotoResults.failed} failed`}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            INVENTORY TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'inventory' && (() => {
          const allCategories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();

          const toggleCategoryFilter = (cat) => {
            setInventoryCategoryFilters(prev =>
              prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
            );
          };

          const searchLower = inventorySearch.trim().toLowerCase();
          const filteredProducts = products.filter(p => {
            const matchesCategory = inventoryCategoryFilters.length === 0 || inventoryCategoryFilters.includes(p.category);
            const matchesSearch = !searchLower ||
              (p.name || '').toLowerCase().includes(searchLower) ||
              (p.articleId || '').toLowerCase().includes(searchLower);
            return matchesCategory && matchesSearch;
          }).sort((a, b) => {
            if (inventorySort === 'name') return (a.name || '').localeCompare(b.name || '');
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return inventorySort === 'oldest' ? aTime - bTime : bTime - aTime;
          });

          const hasActiveFilters = inventoryCategoryFilters.length > 0 || searchLower;

          return (
          <div className="max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold" style={{ color: COLORS.primary }}>
                  Database Inventory ({filteredProducts.length}{hasActiveFilters ? ` of ${products.length}` : ''})
                </h2>
                <button
                  onClick={() => exportInventoryToExcel(filteredProducts)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border hover:bg-gray-50"
                  style={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
                  title="Export the current filtered list (with colors) as an Excel-compatible spreadsheet, for offline cross-checking against physical stock"
                >
                  <FileDown className="w-3.5 h-3.5" /> Export to Excel
                </button>
              </div>

              {/* Select-all + bulk category reassignment — fixes miscategorized
                  products (e.g. Ladies PU items accidentally saved under Gents PU)
                  by letting you pick several products and move them all at once. */}
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="select-all-inventory"
                  checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedProductIds.has(p.id))}
                  onChange={(e) => {
                    setSelectedProductIds(prev => {
                      const next = new Set(prev);
                      if (e.target.checked) filteredProducts.forEach(p => next.add(p.id));
                      else filteredProducts.forEach(p => next.delete(p.id));
                      return next;
                    });
                  }}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: COLORS.secondary }}
                />
                <label htmlFor="select-all-inventory" className="text-xs font-bold text-gray-600 cursor-pointer">
                  Select all {hasActiveFilters ? 'filtered' : ''} ({filteredProducts.length})
                </label>
                {selectedProductIds.size > 0 && (
                  <span className="text-xs font-bold text-gray-400 ml-1">· {selectedProductIds.size} selected</span>
                )}
              </div>

              {selectedProductIds.size > 0 && (
                <div className="sticky top-0 z-10 mb-4 bg-teal-50 border border-teal-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
                  <span className="text-sm font-bold" style={{ color: COLORS.primary }}>
                    {selectedProductIds.size} selected
                  </span>
                  <input
                    list="bulk-move-cat-list"
                    value={bulkCategoryValue}
                    onChange={e => setBulkCategoryValue(e.target.value)}
                    placeholder="Move to category..."
                    disabled={bulkCategoryUpdating}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[180px] focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': COLORS.secondary }}
                  />
                  <datalist id="bulk-move-cat-list">{dynamicCategories.map(c => <option key={c} value={c} />)}</datalist>
                  <button
                    onClick={handleBulkCategoryUpdate}
                    disabled={bulkCategoryUpdating || !bulkCategoryValue}
                    className="px-4 py-1.5 rounded-lg text-white text-sm font-bold disabled:opacity-50 flex items-center gap-2"
                    style={{ backgroundColor: COLORS.secondary }}
                  >
                    {bulkCategoryUpdating
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {bulkCategoryProgress ? `${bulkCategoryProgress.done}/${bulkCategoryProgress.total}` : '...'}</>
                      : 'Apply'}
                  </button>
                  <button
                    onClick={() => setSelectedProductIds(new Set())}
                    disabled={bulkCategoryUpdating}
                    className="text-xs font-bold text-gray-500 hover:text-gray-700"
                  >
                    Clear selection
                  </button>
                </div>
              )}

              {/* Search + sort + multi-category filter bar */}
              <div className="mb-6 space-y-3">
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={inventorySearch}
                      onChange={e => setInventorySearch(e.target.value)}
                      placeholder="Search by article name or number..."
                      className="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': COLORS.secondary }}
                    />
                    {inventorySearch && (
                      <button onClick={() => setInventorySearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <select
                    value={inventorySort}
                    onChange={e => setInventorySort(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 text-sm font-semibold text-gray-600 focus:outline-none focus:ring-2 bg-white"
                    style={{ '--tw-ring-color': COLORS.secondary }}
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                    <option value="name">Name A–Z</option>
                  </select>
                </div>

                {allCategories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {allCategories.map(cat => {
                      const active = inventoryCategoryFilters.includes(cat);
                      return (
                        <button
                          key={cat}
                          onClick={() => toggleCategoryFilter(cat)}
                          className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                            active ? 'text-white' : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'
                          }`}
                          style={active ? { backgroundColor: COLORS.secondary, borderColor: COLORS.secondary } : {}}
                        >
                          {cat}
                        </button>
                      );
                    })}
                    {hasActiveFilters && (
                      <button
                        onClick={() => { setInventoryCategoryFilters([]); setInventorySearch(''); }}
                        className="text-xs font-bold px-3 py-1.5 rounded-full text-red-600 hover:bg-red-50"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                )}
              </div>

              {filteredProducts.length === 0
                ? <p className="text-gray-500 text-sm text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    {hasActiveFilters ? 'No products match your filters.' : 'No products yet.'}
                  </p>
                : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredProducts.map(product => {
                      const displayImage  = product.variations?.[0]?.image;
                      const hasValidImage = displayImage && displayImage.startsWith('http');
                      const isSelected = selectedProductIds.has(product.id);
                      return (
                        <div key={product.id} className={`border rounded-xl overflow-hidden relative group bg-white shadow-sm hover:shadow-lg transition-all ${isSelected ? 'ring-2' : 'border-gray-200'}`} style={isSelected ? { '--tw-ring-color': COLORS.secondary, borderColor: COLORS.secondary } : {}}>

                          {/* Selection checkbox */}
                          <label className={`absolute top-2 left-2 z-10 flex items-center justify-center w-6 h-6 rounded-md bg-white/90 shadow border cursor-pointer ${isSelected ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleProductSelection(product.id)}
                              className="w-4 h-4"
                              style={{ accentColor: COLORS.secondary }}
                            />
                          </label>

                          {/* Action buttons */}
                          <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <button onClick={() => handleEditProduct(product)}
                              className="p-2 bg-white text-blue-600 rounded-lg shadow-md hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => handleDeleteProduct(product.id)}
                              className="p-2 bg-white text-red-600 rounded-lg shadow-md hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                          </div>

                          {/* Warning badge for old base64 images */}
                          {displayImage && !hasValidImage && (
                            <div className="absolute top-2 left-2 z-10 bg-orange-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
                              ⚠ Re-upload for WA
                            </div>
                          )}

                          <div className="h-56 bg-gray-50 border-b border-gray-100">
                            {displayImage
                              ? <img src={displayImage} alt={product.name} className="w-full h-full object-cover bg-gray-100" />
                              : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="text-gray-300 w-12 h-12" /></div>
                            }
                          </div>
                          <div className="p-5">
                            <div className="font-black text-lg truncate tracking-tight" style={{ color: COLORS.primary }}>{product.name}</div>
                            <div className="flex flex-wrap gap-2 mt-3 mb-3">
                              {product.category && <span className="text-[10px] uppercase font-bold bg-gray-100 text-gray-600 px-2 py-1 rounded">{product.category}</span>}
                              {product.audience && <span className="text-[10px] uppercase font-bold bg-gray-100 text-gray-600 px-2 py-1 rounded">{product.audience}</span>}
                            </div>
                            <div className="text-sm font-semibold text-gray-500 border-t pt-3">
                              <div className="flex items-center justify-between mb-2">
                                <span>{product.variations?.length || 0} Colors</span>
                                {hasValidImage && <span className="text-teal-600 text-[10px] font-bold">✓ WA Ready</span>}
                              </div>
                              {product.variations?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {product.variations.map((v, i) => {
                                    const imgOk = v.image && v.image.startsWith('http');
                                    return (
                                      <span key={i}
                                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                          imgOk ? 'bg-gray-50 text-gray-600 border-gray-200' : 'bg-orange-50 text-orange-600 border-orange-200'
                                        }`}
                                        title={imgOk ? 'Image ready' : 'Image missing'}
                                      >
                                        {v.color}{!imgOk && ' ⚠'}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              }
            </div>
          </div>
          );
        })()}

        {/* ════════════════════════════════════════════════════════════════════
            CATEGORIES TAB
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'categories' && (
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="flex items-center text-sm text-gray-500 mb-2 font-bold uppercase tracking-wider">
              <span>Home</span><ChevronRight className="w-4 h-4 mx-1" />
              <span style={{ color: COLORS.primary }}>Category Master</span>
            </div>
            <p className="text-sm text-gray-500 -mt-4">
              This is the single master list of categories. Only categories added here appear as suggestions when adding or bulk-uploading products —
              remove one below to stop suggesting it (existing products keep their category either way). Catalogue PDF link is optional.
            </p>

            <form onSubmit={handleCategorySubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="font-bold text-gray-800">{catForm.id ? 'Edit Category' : 'Add Category'}</h3>
                <p className="text-xs text-gray-500 font-semibold">PDF link is optional — sent instantly via WhatsApp if set.</p>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Category Name <span className="text-red-500">*</span></label>
                  <input required list="cat-list" value={catForm.name}
                    onChange={e => setCatForm({ ...catForm, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:outline-none bg-white"
                    placeholder="e.g. Men PU Slippers" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Catalogue PDF URL</label>
                  <input type="url" value={catForm.pdfLink}
                    onChange={e => setCatForm({ ...catForm, pdfLink: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:outline-none bg-white"
                    placeholder="https://indus-chatbot.indusfootwear.com/catalogues/gents-pu.pdf" />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Use a direct file URL (uploaded to Yash Host), not a Google Drive share link —
                    Drive links are slow/unreliable when sent through WhatsApp.
                  </p>
                </div>
                <div className="md:col-span-2 flex justify-end gap-3">
                  {catForm.id && (
                    <button type="button" onClick={() => setCatForm({ id: null, name: '', pdfLink: '' })}
                      className="px-6 py-2.5 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300">Cancel</button>
                  )}
                  <button type="submit" disabled={isSubmitting}
                    className="px-6 py-2.5 text-white font-bold rounded-lg shadow-md flex items-center disabled:opacity-70"
                    style={{ backgroundColor: COLORS.secondary }}>
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    {catForm.id ? 'Update' : 'Save Category'}
                  </button>
                </div>
              </div>
            </form>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="font-bold text-gray-800">Category Master — Active Categories</h3>
              </div>
              <div className="p-0 overflow-x-auto">
                {categoriesData.length === 0
                  ? <p className="text-gray-500 text-sm text-center py-10">No category links yet.</p>
                  : (
                    <table className="w-full text-sm text-left border-collapse">
                      <thead className="bg-white border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                        <tr>
                          <th className="px-6 py-4 font-bold w-1/3">Category</th>
                          <th className="px-6 py-4 font-bold">Link</th>
                          <th className="px-6 py-4 font-bold text-right w-28">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {categoriesData.map(cat => (
                          <tr key={cat.id} className="bg-white hover:bg-gray-50/50">
                            <td className="px-6 py-4 font-bold text-gray-800">{cat.name}</td>
                            <td className="px-6 py-4">
                              {cat.pdfLink
                                ? <a href={cat.pdfLink} target="_blank" rel="noreferrer"
                                    className="text-blue-600 hover:underline flex items-center font-medium">
                                    <FileText className="w-4 h-4 mr-1.5" /> View Link
                                  </a>
                                : <span className="text-gray-400 italic">No link</span>}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-2">
                                <button onClick={() => handleEditCategory(cat)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => handleDeleteCategory(cat.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            BOT TESTER TAB — testing only, mimics Twilio's webhook POST
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'bot_tester' && (
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="flex items-center text-sm text-gray-500 mb-2 font-bold uppercase tracking-wider">
              <span>Home</span><ChevronRight className="w-4 h-4 mx-1" />
              <span style={{ color: COLORS.primary }}>Bot Tester</span>
            </div>

            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold rounded-lg px-4 py-2.5 flex items-center">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              Testing only — sends to your Flask server's /whatsapp-test route, which runs the real bot logic but intercepts outbound sends instead of calling Meta's Graph API. No WhatsApp Cloud API credentials needed locally.
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5">Bot Server URL</label>
                <input value={botEndpoint} onChange={e => setBotEndpoint(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:outline-none bg-white font-mono"
                  placeholder="http://localhost:3000" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5">Test "From" Number</label>
                <input value={testFromNumber} onChange={e => setTestFromNumber(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:outline-none bg-white font-mono"
                  placeholder="whatsapp:+919999999999" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5">Admin Key</label>
                <input type="password" value={botAdminKey} onChange={e => setBotAdminKey(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:outline-none bg-white font-mono"
                  placeholder="matches ADMIN_API_KEY on server" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" style={{ height: '480px' }}>
              <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center" style={{ backgroundColor: COLORS.primary }}>
                <span className="text-white font-bold text-sm flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2" /> Simulated WhatsApp Chat
                </span>
                <button onClick={resetTestSession}
                  className="text-white/80 hover:text-white text-xs font-bold flex items-center bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-lg">
                  <RotateCcw className="w-3 h-3 mr-1.5" /> Reset (send "menu")
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" style={{ backgroundImage: 'radial-gradient(circle, #e5e7eb 1px, transparent 1px)', backgroundSize: '16px 16px' }}>
                {chatLog.length === 0 && (
                  <p className="text-center text-gray-400 text-xs mt-10">Send "hi" below to start testing the bot flow.</p>
                )}
                {chatLog.map((msg, i) => (
                  <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm shadow-sm whitespace-pre-wrap ${
                      msg.sender === 'user' ? 'bg-teal-600 text-white' : 'bg-white text-gray-800 border border-gray-200'
                    }`}>
                      {msg.mediaUrl && (
                        msg.mediaUrl.toLowerCase().endsWith('.pdf')
                          ? <a href={msg.mediaUrl} target="_blank" rel="noreferrer"
                              className="flex items-center text-blue-600 underline text-xs font-bold mb-1.5">
                              <FileText className="w-4 h-4 mr-1" /> PDF attachment (open)
                            </a>
                          : <img src={msg.mediaUrl} alt="media" className="rounded-lg mb-1.5 max-h-48 object-contain bg-gray-100" />
                      )}
                      {msg.text}
                    </div>
                  </div>
                ))}
                {isSendingTest && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm shadow-sm">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  </div>
                )}
              </div>

              <div className="p-3 border-t border-gray-200 bg-white flex gap-2">
                <input value={testInput} onChange={e => setTestInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendTestMessage()}
                  placeholder="Type a message... e.g. hi, 2225, #, next"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:outline-none" />
                <button onClick={() => sendTestMessage()} disabled={isSendingTest}
                  className="px-4 py-2 text-white font-bold rounded-lg shadow-md disabled:opacity-50"
                  style={{ backgroundColor: COLORS.secondary }}>
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {['hi', 'browse categories', '1', '2225', 'next', 'list', '#', '5'].map(quick => (
                <button key={quick} onClick={() => sendTestMessage(quick)}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg font-mono">
                  {quick}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            ACTIVITY TAB — who's using the bot, what they're searching for,
            and whether it's finding real matches. Sourced from the
            activity_log Firestore collection server.py writes to on every
            meaningful search.
        ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'activity' && (() => {
          const RESULT_LABELS = {
            exact_match: { label: 'Found', color: 'text-teal-700 bg-teal-50' },
            series: { label: 'Series shown', color: 'text-teal-700 bg-teal-50' },
            prefix: { label: 'Prefix list shown', color: 'text-blue-700 bg-blue-50' },
            partial: { label: 'Partial matches', color: 'text-blue-700 bg-blue-50' },
            suggestion: { label: 'Suggested', color: 'text-orange-700 bg-orange-50' },
            suggestion_confirmed: { label: 'Confirmed suggestion', color: 'text-teal-700 bg-teal-50' },
            no_match: { label: 'Not found', color: 'text-red-700 bg-red-50' },
          };

          const searchLower = activitySearch.trim().toLowerCase();
          const filtered = activityLog.filter(entry => {
            const matchesFilter = activityResultFilter === 'all' || entry.result_type === activityResultFilter;
            const matchesSearch = !searchLower ||
              (entry.phone || '').toLowerCase().includes(searchLower) ||
              (entry.query || '').toLowerCase().includes(searchLower) ||
              (entry.matched_name || '').toLowerCase().includes(searchLower);
            return matchesFilter && matchesSearch;
          });

          // Top searched queries — aggregated from what's currently loaded
          const queryCounts = {};
          activityLog.forEach(entry => {
            const q = (entry.query || '').trim();
            if (!q) return;
            queryCounts[q] = (queryCounts[q] || 0) + 1;
          });
          const topQueries = Object.entries(queryCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);

          const noMatchCount = activityLog.filter(e => e.result_type === 'no_match').length;
          const uniquePhones = new Set(activityLog.map(e => e.phone).filter(Boolean)).size;

          // Group by phone number — total searches per customer, their most-
          // searched product, and their last-seen time. Sorted by search
          // count descending, so the most engaged customers float to top.
          const byPhone = {};
          activityLog.forEach(entry => {
            const phone = entry.phone;
            if (!phone) return;
            if (!byPhone[phone]) {
              byPhone[phone] = { phone, count: 0, queryCounts: {}, lastSeen: null, noMatchCount: 0 };
            }
            const bucket = byPhone[phone];
            bucket.count += 1;
            if (entry.result_type === 'no_match') bucket.noMatchCount += 1;
            const q = (entry.query || '').trim();
            if (q) bucket.queryCounts[q] = (bucket.queryCounts[q] || 0) + 1;
            const ts = entry.createdAt ? new Date(entry.createdAt).getTime() : 0;
            if (!bucket.lastSeen || ts > bucket.lastSeen) bucket.lastSeen = ts;
          });

          const customerRows = Object.values(byPhone).map(c => {
            const topQuery = Object.entries(c.queryCounts).sort((a, b) => b[1] - a[1])[0];
            return {
              ...c,
              topQuery: topQuery ? topQuery[0] : '—',
              topQueryCount: topQuery ? topQuery[1] : 0,
            };
          }).sort((a, b) => b.count - a.count);

          const customerSearchLower = activitySearch.trim().toLowerCase();
          const filteredCustomers = customerRows.filter(c =>
            !customerSearchLower ||
            c.phone.toLowerCase().includes(customerSearchLower) ||
            c.topQuery.toLowerCase().includes(customerSearchLower)
          );

          return (
            <div className="max-w-6xl mx-auto space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center text-sm text-gray-500 font-bold uppercase tracking-wider">
                  <span>Home</span><ChevronRight className="w-4 h-4 mx-1" />
                  <span style={{ color: COLORS.primary }}>Activity</span>
                </div>
                <button
                  onClick={() => setActivityRefreshKey(k => k + 1)}
                  disabled={activityLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border hover:bg-gray-50 disabled:opacity-50"
                  style={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
                >
                  <RotateCcw className={`w-3.5 h-3.5 ${activityLoading ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>

              {activityLoading && (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: COLORS.secondary }} />
                </div>
              )}

              {!activityLoading && activityLog.length === 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">
                  No activity logged yet — this fills in as customers search the bot on WhatsApp.
                </div>
              )}

              {!activityLoading && activityLog.length > 0 && (
                <>
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-2xl font-extrabold" style={{ color: COLORS.primary }}>{activityLog.length}</p>
                      <p className="text-xs font-bold text-gray-500 uppercase mt-1">Searches (last 500)</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-2xl font-extrabold" style={{ color: COLORS.primary }}>{uniquePhones}</p>
                      <p className="text-xs font-bold text-gray-500 uppercase mt-1">Unique numbers</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-2xl font-extrabold text-red-600">{noMatchCount}</p>
                      <p className="text-xs font-bold text-gray-500 uppercase mt-1">Not found</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-2xl font-extrabold" style={{ color: COLORS.primary }}>
                        {activityLog.length ? Math.round(((activityLog.length - noMatchCount) / activityLog.length) * 100) : 0}%
                      </p>
                      <p className="text-xs font-bold text-gray-500 uppercase mt-1">Match rate</p>
                    </div>
                  </div>

                  {/* Top searches */}
                  {topQueries.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h3 className="text-xs font-bold text-gray-500 uppercase mb-3">Most searched</h3>
                      <div className="flex flex-wrap gap-2">
                        {topQueries.map(([q, count]) => (
                          <span key={q} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                            {q} <span className="text-gray-400">× {count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* View mode toggle */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setActivityViewMode('customers')}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        activityViewMode === 'customers' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      style={activityViewMode === 'customers' ? { backgroundColor: COLORS.secondary } : {}}
                    >
                      👤 By Customer ({customerRows.length})
                    </button>
                    <button
                      onClick={() => setActivityViewMode('log')}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        activityViewMode === 'log' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      style={activityViewMode === 'log' ? { backgroundColor: COLORS.secondary } : {}}
                    >
                      📋 Full Search Log
                    </button>
                  </div>

                  {/* Filters — shared search box, result filter only relevant in log view */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={activitySearch}
                        onChange={e => setActivitySearch(e.target.value)}
                        placeholder={activityViewMode === 'customers' ? "Search by phone number or their top search..." : "Search by phone number, query, or matched article..."}
                        className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': COLORS.secondary }}
                      />
                    </div>
                    {activityViewMode === 'log' && (
                      <select
                        value={activityResultFilter}
                        onChange={e => setActivityResultFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 text-sm font-semibold text-gray-600 bg-white focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': COLORS.secondary }}
                      >
                        <option value="all">All results</option>
                        {Object.entries(RESULT_LABELS).map(([key, { label }]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* BY CUSTOMER — grouped view, ranked by usage/interest level */}
                  {activityViewMode === 'customers' && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Phone</th>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Searches</th>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Interest Level</th>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Most Searched</th>
                              <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Last Active</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredCustomers.slice(0, 200).map(c => {
                              const maxCount = customerRows[0]?.count || 1;
                              const interestPct = Math.round((c.count / maxCount) * 100);
                              const interestLabel = c.count >= 10 ? 'Very High' : c.count >= 5 ? 'High' : c.count >= 2 ? 'Medium' : 'Low';
                              const interestColor = c.count >= 10 ? 'bg-teal-500' : c.count >= 5 ? 'bg-teal-400' : c.count >= 2 ? 'bg-amber-400' : 'bg-gray-300';
                              return (
                                <tr key={c.phone} className="hover:bg-gray-50">
                                  <td className="px-4 py-2.5 font-mono font-semibold text-gray-800 whitespace-nowrap">{c.phone}</td>
                                  <td className="px-4 py-2.5 text-gray-600 font-bold">{c.count}</td>
                                  <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                      <div className="w-20 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                        <div className={`h-full rounded-full ${interestColor}`} style={{ width: `${interestPct}%` }} />
                                      </div>
                                      <span className="text-[11px] font-bold text-gray-500">{interestLabel}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2.5 text-gray-700">
                                    {c.topQuery} {c.topQueryCount > 1 && <span className="text-gray-400">(× {c.topQueryCount})</span>}
                                  </td>
                                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                                    {c.lastSeen ? new Date(c.lastSeen).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {filteredCustomers.length === 0 && (
                        <div className="text-center text-sm text-gray-400 py-8">No customers match your search.</div>
                      )}
                    </div>
                  )}

                  {/* FULL SEARCH LOG — flat, one row per search */}
                  {activityViewMode === 'log' && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">When</th>
                            <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Phone</th>
                            <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Searched</th>
                            <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Result</th>
                            <th className="text-left px-4 py-2.5 font-bold text-gray-500 text-xs uppercase">Matched</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filtered.slice(0, 200).map(entry => {
                            const rt = RESULT_LABELS[entry.result_type] || { label: entry.result_type || '—', color: 'text-gray-600 bg-gray-100' };
                            const when = entry.createdAt ? new Date(entry.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
                            return (
                              <tr key={entry.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{when}</td>
                                <td className="px-4 py-2.5 font-mono text-gray-700 whitespace-nowrap">{entry.phone || '—'}</td>
                                <td className="px-4 py-2.5 font-semibold text-gray-800">{entry.query || '—'}</td>
                                <td className="px-4 py-2.5">
                                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${rt.color}`}>{rt.label}</span>
                                </td>
                                <td className="px-4 py-2.5 text-gray-500">{entry.matched_name || (entry.match_count ? `${entry.match_count} results` : '—')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {filtered.length === 0 && (
                      <div className="text-center text-sm text-gray-400 py-8">No results match your filters.</div>
                    )}
                    {filtered.length > 200 && (
                      <div className="text-center text-xs text-gray-400 py-3 border-t border-gray-100">
                        Showing first 200 of {filtered.length} matching entries — narrow your search to see more specific results.
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

      </div>
    </div>
  );
}